import type { StartIntent, TableStatusResponse } from "@bing/shared";
import { createStore } from "zustand/vanilla";
import type { GameResult } from "./connection";
import { playSfx } from "../sound/sfx";
import {
  joinErrorFa,
  localizedReason,
  readTableEnd,
  tableFateLine,
  tableJoinReason,
  type TableEnd,
} from "./join-error";
import {
  TableOverError,
  type ClaimableInfo,
  type ClockInfo,
  type LeavePenaltyInfo,
  type LeftInfo,
  type LobbyState,
  type OnlineConnector,
  type OnlineHandle,
  type PausedInfo,
  type ReconnectingInfo,
  type TurnInfo,
} from "./online-connection";
import type { TableSettings } from "./private-table";
import type { RematchStateFrame } from "./rematch";
import { isSeatMoved } from "./seat-takeover";

/**
 * The container phase. Note this is distinct from `lobby.phase`
 * (`"waiting" | "starting"`): both waiting and the starting-countdown live under
 * the single `"waiting"` container phase — the WaitingRoom decides which to show.
 */
export type OnlinePhase = "connecting" | "waiting" | "playing" | "gameover" | "error";

/**
 * How long «حریف شما برای ۶۰ ثانیه متوالی حضور نداشت — می‌توانید بدون جریمه خارج شوید» stays up
 * (operator, 2026-09-15, reversing their own «it persists» of the same morning). A notice that never
 * goes away becomes the thing covering the board; the exit dialog still says the exit is free for
 * as long as it is.
 */
export const FREE_EXIT_NOTICE_MS = 10_000;

/** How long the server's reason for a refused «شروع» / «بازی دوباره» stays on screen. */
export const START_REFUSED_NOTICE_MS = 6_000;

export interface OnlineState<M, V> {
  phase: OnlinePhase;
  /** Our assigned seat (from the `"seat"` message), or null before it arrives. */
  seat: number | null;
  /** Whether we host the (private) table — gates the start/resume controls. */
  isHost: boolean;
  lobby: LobbyState | null;
  /**
   * THE ROSTER THE MATCH ENDED WITH — snapshotted on the FIRST `gameover` (or, when that frame
   * beat every lobby frame, on the first roster after it) and never moved again; null until then.
   *
   * ⚠️ THE LIVE {@link lobby} KEEPS CHANGING AFTER THE END, AND THAT WAS THE BUG (session B,
   * 2026-10-08). The server still runs the ordinary takeover on every post-terminal departure —
   * the end card's own «خروج» included — so whoever closed their card first became
   * `{isBot:true, name:"Bot 2"}` on every other screen, and the result card, the felt behind it
   * and the chat roster all renamed them «بات N». Everything that NAMES people at a finished table
   * reads this; everything that asks who is still THERE (the rematch) reads the live one.
   */
  endLobby: LobbyState | null;
  /**
   * `left` frames that arrived AFTER the end — recorded, never sounded or toasted.
   *
   * ⚠️ RECORDED BECAUSE AN OLD GAME-SERVER SAYS NOTHING ELSE. It sends no `rematch-state`, so this
   * and the roster's bot flip are the only signals the rematch decision has that every other player
   * has left the finished table (whether anybody still OWNS it reads off the live roster's crown —
   * card-a5 item 8).
   */
  endDepartures: readonly LeftInfo[];
  /** CONTRACT C1 — the server's last verdict on this private table's rematch; null = never heard
   *  (an old game-server, a public table), which the rematch decision reads as UNKNOWN. */
  rematchState: RematchStateFrame | null;
  /** The finished room's socket closed under us after `gameover` — nothing can be offered, heard
   *  or followed through it any more (an offer that already arrived still can). */
  roomGone: boolean;
  /** The table's rules, mirrored from the lobby (or the host's create request). */
  settings: TableSettings | null;
  view: V | null;
  /** The seat's server-authoritative legal moves (the table also derives its own). */
  legal: readonly M[];
  /** The live turn + its timer deadline, or null between turns. */
  turn: TurnInfo | null;
  /** CHESS ONLY — the latest server clock banks for a timed chess table (null for the card games
   *  + untimed chess, which never send a clock frame). The table ticks the side to move from it. */
  clock: ClockInfo | null;
  /** A dropped player (pause policy), or null while everyone is connected. */
  paused: PausedInfo | null;
  /** A seat dropped and its reconnect grace is counting down, or null. EVERY game populates this
   *  now — the frame used to be gated to chess/backgammon, so a card table heard nothing until the
   *  bot takeover 120s later. Chess/تخته نرد drive the full-screen "opponent reconnecting… mm:ss"
   *  overlay from it; the card games + منچ draw a small non-blocking pill and play on.
   *  ⚠️ ONE SLOT, so on a 4-seat table a second drop overwrites the first (and either return clears
   *  it). The `left` banner still reports each seat individually. */
  reconnecting: ReconnectingInfo | null;
  /** CHESS ONLY — the present player may CLAIM the win/draw (opponent gone), or null. Drives the
   *  «ادعای برد» prompt. Card games never populate it. */
  claimable: ClaimableInfo | null;
  /** Transient banner: a player who left and was replaced by a bot (bot policy). */
  leftNotice: LeftInfo | null;
  /**
   * A seat's disconnect GRACE ran out and a bot took the chair (`left` with `reason:
   * "disconnected"`), held for {@link FREE_EXIT_NOTICE_MS} — what the free-exit notice is keyed on.
   *
   * ⚠️ AN EVENT, NOT A STATE (2026-09-15). The notice used to follow `lastHumanStanding`, the exit
   * dialog's predicate, which is also true for a whole match on any table that dealt short-handed
   * (`fullAtDeal === false`) — so every bot-filled حکم/شلم/بی‌دل/پاسور/بلوف/منچ table showed «your
   * opponent was absent for 60 seconds» from the deal to the end, on the felt's top row, over the
   * trump. The sentence is true only at the moment this frame arrives. Recorded for EVERY game; the
   * container decides which games show it.
   */
  freeExitNotice: LeftInfo | null;
  /**
   * WE were removed from a PUBLIC table for inactivity (five consecutive auto-played turns).
   *
   * Separate from {@link leftNotice} because it is the opposite kind of message: that one is a
   * transient banner ABOUT someone else and auto-dismisses; this one is addressed TO the reader
   * and must not disappear on a timer.
   *
   * ⚠️ WHAT THE TABLE UNDERNEATH ACTUALLY DOES, checked rather than assumed: the room clears
   * `seatBySession` only when the SOCKET leaves, and an idle removal does not close it — so this
   * client keeps receiving that seat's frames and watches a BOT play its hand under its own name.
   * Not a frozen screen (the 2026-07-23 report) but no better: either way the player is left
   * looking at a table that is no longer theirs with nothing telling them why. Hence a covering
   * screen whose only exit is the lobby, rather than a banner over a live-looking table.
   */
  removedForIdle: LeftInfo | null;
  /** Dismiss the removal notice (the acknowledge button) — the container then leaves for the lobby. */
  acknowledgeRemoval: () => void;
  /**
   * THE HOST REMOVED US from their private table («اخراج», 2026-09-02).
   *
   * Kept SEPARATE from {@link removedForIdle} even though both cover the screen: they are not the
   * same message. An idle removal is a rule the player triggered and the copy says so; this one is
   * somebody else's decision, and telling them they were inactive would be a plain untruth.
   */
  kickedOut: boolean;
  /**
   * A NEWER COPY OF THE APP TOOK THIS SEAT BACK (card-74). Its own flag, not the join-failure
   * `phase: "error"`: a takeover happens to a seat this copy HAD, and the effects keyed on a failed
   * join (the silent straight-back, an act routed through the table) must not read it as one.
   */
  takenOver: boolean;
  /** «بازی دوباره» — the successor table the private HOST has opened, or null while there is no
   *  offer. One value drives every seat's button (see `rematchAction`). */
  rematchRoomId: string | null;
  /** Host-only: ask the server to open it. Re-checked server side. */
  requestRematch: () => void;
  /** The server's reason for refusing the host's «شروع» / «بازی دوباره» (maintenance), shown for
   *  {@link START_REFUSED_NOTICE_MS} and then cleared; null otherwise. */
  startRefused: string | null;
  /** Dismiss the removal notice — the container then leaves for the lobby. */
  acknowledgeKick: () => void;
  /** Host-only: remove the player at `seat` («اخراج»). A no-op without a live connection. */
  kick: (seat: number) => void;
  /** The host closed this waiting-room table (their name). The container shows the
   *  notice, sounds it, and returns to the lobby — the table no longer exists. */
  closedBy: string | null;
  /**
   * Whether the table was closed while a game was actually IN PROGRESS.
   *
   * The server sends one `table-closed` for two very different things — the host walking out of
   * the WAITING room, and the host quitting a live game for good («خروج بدون بازگشت», which
   * cancels the whole table). Those need different words: «the host closed the table» is true of
   * both but tells a player whose game just vanished mid-hand almost nothing. The phase at the
   * moment the notice arrives is the discriminator, and the client already has it — so this
   * distinction costs no server change.
   */
  closedMidGame: boolean;
  /** This waiting-room table auto-expired (never started within the idle window). The
   *  container shows the «میز منقضی شد» notice, sounds it, and returns to the lobby. */
  expiredByIdle: boolean;
  result: GameResult | null;
  error: string | null;
  /**
   * HOW THE TABLE WE CAME BACK TO ENDED (session B, item 3) — set with the «این بازی تمام شده است»
   * error when a «ادامه بازی» return found the table FINISHED and the status route said how
   * (contract C2's `end`); null otherwise, including an old api that sends no `end`.
   */
  tableEnd: TableEnd | null;
  send: (move: M) => void;
  /** Host-only: push updated table settings while in the lobby. */
  sendSettings: (settings: TableSettings) => void;
  /** Host-only: start the private table — the {@link StartIntent} says which start
   * was pressed (bot-fill vs a full human table). */
  start: (intent?: StartIntent) => void;
  /** Host-only, `teamSelect` phase: apply a seat arrangement (who partners with
   * whom) — `order[newSeat]` = the CURRENT seat of the player moving there. */
  setTeams: (order: readonly number[]) => void;
  /** Host-only: resume a paused game (fill the dropped seat with a bot). */
  resume: () => void;
  /** CHESS ONLY — the present player claims the win/draw while their opponent is gone. */
  claim: () => void;
  /** Start the join — once; never for a store already closed ({@link disconnect} / {@link drop} first
   *  ⇒ nothing opens). A no-op unless the store was built with `deferConnect`, which already did it. */
  connect: () => void;
  disconnect: () => void;
  /**
   * WALK AWAY FROM A TABLE THAT HAS NOT SHOWN ITSELF YET — the connecting screen's exit (session B,
   * item 8). Calls the join off ({@link disconnect}'s abort) and closes whatever already landed the
   * way a DROPPED socket closes, never consented: the server holds the seat for its grace and the
   * saved «ادامه بازی» ticket stays good. ⚠️ Not {@link disconnect}: between the join landing and
   * the first frame that would be a CONSENTED close — at a private شطرنج/تخته نرد under
   * «توقف»/«ادعا», the claimable-at-once door to a rated loss.
   *
   * ALSO THE CRASH'S CLOSE (card-a5 item 3): when the root `ErrorBoundary` replaces the app with its
   * fallback, `OnlineGame`'s unmount cleanup closes the table with this, not {@link disconnect} — a
   * crash is a lost connection, never a «خروج» (`game/crash-return.ts`).
   */
  drop: () => void;
  /**
   * CARD-98 3.3 — the close about to follow comes from a screen that shows NO CARDS yet: the
   * «آماده‌سازی کارت‌ها» screen, already past the waiting room (the deal landed, the artwork has not).
   * Says so ahead of the close, as {@link disconnect} does from the waiting room itself; the server
   * takes it only within 3 s of the deal, from a seat that has not played. Call it right before the
   * exit that closes the table.
   */
  leaveWaiting: () => void;
  /** «خروج بدون بازگشت» — leave PERMANENTLY (no reclaimable seat held). */
  /** «خروج بدون بازگشت». Resolves with what the leave cost (null ⇒ the server never said). */
  leaveForever: () => Promise<LeavePenaltyInfo | null>;
  /** FEATURE 3 — host-only: the server minted a reconnection token for a friend to be invited
   *  into a specific bot seat mid-game. The container reacts (creates the friend-invite carrying
   *  it) then clears it. Null when nothing is pending. */
  seatTokenMinted: SeatTokenMinted | null;
  /** Host-only: ask the server to mint a seat-fill token for `toUserId` at `seat`. */
  mintSeatToken: (seat: number, toUserId: string) => void;
  /** Consume the minted-token payload once the container has created the invite. */
  clearSeatTokenMinted: () => void;
}

/** The server's reply to a seat-fill mint request (feature 3). */
export interface SeatTokenMinted {
  seat: number;
  toUserId: string;
  roomId: string;
  /** The `roomId:token` reconnection token the invited friend `client.reconnect`s with. */
  token: string;
  game?: string;
}

/** What the store needs to explain a FAILED join (a stale invite link). Omitted
 *  (every existing caller, the tests) ⇒ the generic Colyseus-derived wording. */
export interface OnlineStoreOptions {
  /** The room we tried to join — the id whose fate we can look up. */
  roomId?: string | undefined;
  /** `GET /tables/:roomId/status`. */
  tableStatus?: ((roomId: string) => Promise<TableStatusResponse>) | undefined;
  /** A «ادامه بازی» RETURN — the player was seated at this table, so a finished one may say how it
   *  ended for them ({@link OnlineState.tableEnd}). An invite into somebody else's finished table
   *  gets the first line only: the how-lines speak to a participant. */
  returning?: boolean | undefined;
  /**
   * CONNECT ON COMMIT (card-a5 item 3): open nothing at creation — {@link OnlineState.connect} does.
   * For a store built DURING RENDER (`OnlineGame`'s `useMemo`): a render that never commits — it threw,
   * or React threw it away — runs no effect, so nothing would ever close a socket it had opened, and
   * that socket would sit on the seat, acknowledged, while nobody plays it (a public table charges
   * those idle turns). The committed mount effect calls `connect()`. Omitted (every other caller, the
   * tests) ⇒ the join starts at creation, as it always did.
   */
  deferConnect?: boolean | undefined;
}

/**
 * Framework-free store for an online match of ANY multi-human game: connects via
 * the injected connector and mirrors the server (lobby → redacted table view →
 * turn/pause lifecycle → game over). The server validates every move; the client
 * only renders and sends intent. Testable with a fake connector — no WebSocket.
 * (Generic sibling of `createGameStore`; parameterised by the move `M`/view `V`.)
 */
export function createOnlineStore<M, V>(
  connector: OnlineConnector<M, V>,
  opts: OnlineStoreOptions = {},
) {
  let handle: OnlineHandle<M> | null = null;
  /**
   * THE TABLE WAS LEFT BEFORE THE JOIN LANDED. `disconnect` could only leave a `handle` it already
   * had, so a join still in flight at unmount — slow, or the patient «ادامه بازی» rejoin retrying a
   * 4214 — landed afterwards into a store nobody renders: a live seat, a lifecycle-managed socket,
   * and no one ever to leave it. Aborting tells the connector to stop asking, and a join that lands
   * anyway is DROPPED on arrival (below) — closed non-consented, the way a dead socket closes, so
   * the server gives the seat its grace instead of reading a deliberate leave.
   */
  const left = new AbortController();

  /**
   * A join-by-id just failed (a stale invite). Colyseus only knows «not found» /
   * «is locked», so ASK what actually became of the table and replace the generic
   * reason with the true one. Best-effort and late-arriving: the generic message is
   * already on screen, this only improves it — and it never overwrites a NEWER error
   * or a table that meanwhile connected.
   */
  const explainJoinFailure = (error: unknown, generic: string | null): void => {
    const roomId = opts.roomId;
    const lookup = opts.tableStatus;
    if (!roomId || !lookup) return;
    // Keep what the table recorded: a «ادامه بازی» return to a FINISHED table also says HOW
    // (session B, item 3) — on this path too, a room already disposed (`4212`).
    const seen: { status: TableStatusResponse | null } = { status: null };
    const remembering = (id: string): Promise<TableStatusResponse> =>
      lookup(id).then((status) => {
        seen.status = status;
        return status;
      });
    void tableJoinReason(error, roomId, remembering).then((reason) => {
      if (!reason || reason === generic) return;
      const tableEnd =
        opts.returning === true && seen.status?.state === "finished"
          ? readTableEnd(seen.status)
          : null;
      store.setState((s) =>
        s.phase === "error" && s.error === generic ? { error: reason, tableEnd } : {},
      );
    });
  };

  const store = createStore<OnlineState<M, V>>(() => ({
    phase: "connecting",
    seat: null,
    isHost: false,
    lobby: null,
    endLobby: null,
    endDepartures: [],
    rematchState: null,
    roomGone: false,
    settings: null,
    view: null,
    legal: [],
    turn: null,
    clock: null,
    paused: null,
    reconnecting: null,
    claimable: null,
    leftNotice: null,
    freeExitNotice: null,
    removedForIdle: null,
    kickedOut: false,
    takenOver: false,
    rematchRoomId: null,
    startRefused: null,
    closedBy: null,
    closedMidGame: false,
    expiredByIdle: false,
    result: null,
    error: null,
    tableEnd: null,
    send: (move) => handle?.send(move),
    sendSettings: (settings) => handle?.sendSettings?.(settings),
    start: (intent) => handle?.start?.(intent),
    setTeams: (order) => handle?.setTeams?.(order),
    resume: () => handle?.resume?.(),
    claim: () => handle?.claim?.(),
    connect: () => connect(),
    disconnect: () => {
      left.abort();
      // card-98 item 3a — leaving while the WAITING ROOM is what this phone shows (no cards yet):
      // say so ahead of the close, so a table that dealt meanwhile does not hold the seat.
      if (store.getState().phase === "waiting") handle?.leaveWaiting?.();
      handle?.leave();
      handle = null;
    },
    drop: () => {
      left.abort(); // a join still in flight is dropped on arrival (below)
      handle?.drop();
      handle = null;
    },
    leaveWaiting: () => handle?.leaveWaiting?.(),
    leaveForever: async () => {
      const info = (await handle?.leaveForever?.()) ?? null;
      handle = null;
      return info;
    },
    seatTokenMinted: null,
    mintSeatToken: (seat, toUserId) => handle?.mintSeatToken?.(seat, toUserId),
    clearSeatTokenMinted: () => store.setState({ seatTokenMinted: null }),
    acknowledgeRemoval: () => store.setState({ removedForIdle: null }),
    acknowledgeKick: () => store.setState({ kickedOut: false }),
    kick: (seat) => handle?.kick?.(seat),
    requestRematch: () => handle?.requestRematch?.(),
  }));

  /**
   * THE JOIN — started once, and never for a store already closed (a `deferConnect` store that was
   * dropped before its table ever committed opens nothing at all).
   */
  let connected = false;
  const connect = (): void => {
    if (connected || left.signal.aborted) return;
    connected = true;
    connector(
      {
        onSeat: (seat, isHost) => store.setState({ seat, isHost: isHost ?? false }),
        onRematchOffered: ({ roomId }) => store.setState({ rematchRoomId: roomId }),
        onRematchState: (rematchState) => store.setState({ rematchState }),
        onRoomGone: () => store.setState({ roomGone: true }),
        // A refused start says why for a few seconds, then goes by itself (the `leftNotice` pattern).
        onStartRefused: (message) => {
          playSfx("notify");
          store.setState({ startRefused: message });
          setTimeout(() => {
            store.setState((s) => (s.startRefused === message ? { startRefused: null } : {}));
          }, START_REFUSED_NOTICE_MS);
        },
        onLobby: (lobby) =>
          store.setState((s) => {
            // Private tables carry their settings on every lobby frame; keep the last
            // known settings if a frame omits them.
            const settings = lobby.settings ?? s.settings;
            // A `gameover` that beat every roster frame: the first roster after it IS the end
            // roster (the terminal's own lobby frame follows its `gameover` on the wire).
            const end = s.result !== null && s.endLobby === null ? { endLobby: lobby } : {};
            // Once the game has started, keep the frame but don't fall back to waiting.
            return s.phase === "playing" || s.phase === "gameover"
              ? { lobby, settings, ...end }
              : { lobby, settings, phase: "waiting", ...end };
          }),
        onState: (view, legal) =>
          // WHOLESALE replace: every state frame is the server's full truth. A frame
          // arriving after an error (a lifecycle reconnect re-emitted state) also
          // clears the stale error so the table recovers instead of sticking.
          store.setState((s) =>
            s.phase === "gameover"
              ? { view, legal }
              : { view, legal, phase: "playing", error: null },
          ),
        onGameOver: (result) => {
          // The chime rides the overlay REVEAL (`useOutcomeSfx`), not this frame —
          // see the note in the solo store.
          //
          // The FIRST gameover freezes the roster ({@link OnlineState.endLobby}). The server
          // RE-SENDS gameover on every post-terminal takeover (`afterAdvance` → `syncState`), each
          // one after the departure it reports, so a later frame must never re-take the snapshot.
          // ⚠️ The CROWN is not frozen with it (card-a5 item 8): `isHost` stays live, because after
          // the end the game-server hands it to a guest once the owner has gone — and that guest
          // owns the rematch.
          store.setState((s) => ({
            result,
            phase: "gameover",
            ...(s.result === null ? { endLobby: s.lobby } : {}),
          }));
        },
        // No "your turn" chime — the beep was disliked and is gone. The ring on the felt
        // is the turn cue now.
        onTurn: (turn) => store.setState({ turn }),
        // The turn ended with no human on turn (bots playing / paused / ended) — clear the
        // countdown so it never freezes at 0 on a departed seat's stale deadline.
        onTurnEnd: () => store.setState({ turn: null }),
        // CHESS — fresh server clock banks (a move / the deal / a reconnect re-sync). The table
        // renders each side's time and ticks the side to move from this snapshot.
        onClock: (clock) => store.setState({ clock }),
        onPaused: (paused) => store.setState({ paused, reconnecting: null }),
        // The opponent returned (or a paused game resumed): drop every "opponent away" overlay.
        onResumed: () => store.setState({ paused: null, reconnecting: null, claimable: null }),
        // A seat dropped and its grace is counting down. Sent for EVERY game since the announce was
        // un-gated (chess/تخته نرد render the full-screen overlay, the rest a non-blocking pill).
        onReconnecting: (reconnecting) => store.setState({ reconnecting }),
        // CHESS — the grace expired (or the max-pause elapsed): the present player may now claim. The
        // reconnecting / pause overlay is replaced by the claim prompt.
        onClaimable: (claimable) => store.setState({ claimable, reconnecting: null, paused: null }),
        // A reconnect re-established the seat on a FRESH socket: the old session's
        // transient frames (an armed turn timer, a pause, a left-banner, a chess
        // reconnecting/claimable overlay) may be stale — drop them; the server re-emits
        // whatever is still true right after.
        // Removed by the host: cover the screen, and drop the transient overlays under it — the
        // table below is no longer ours and every frame it still sends is about somebody else's game.
        onKicked: () => {
          playSfx("notify");
          store.setState({ kickedOut: true, reconnecting: null, paused: null });
        },
        // A NEWER COPY OF THE APP TOOK THIS SEAT BACK (card-74): the connection is already released.
        // The container covers the table with the «why you could not join» screen, which goes to the
        // lobby — and nothing saved is touched: the ticket and the waiting seat on this device belong
        // to the copy playing now.
        onTakenOver: () =>
          store.setState({ takenOver: true, reconnecting: null, paused: null, claimable: null }),
        onReset: () =>
          store.setState({
            turn: null,
            paused: null,
            reconnecting: null,
            claimable: null,
            leftNotice: null,
            freeExitNotice: null,
          }),
        onLeft: (info) => {
          /*
           * AFTER THE END: RECORD IT, SAY NOTHING (session B, 2026-10-08). A post-terminal departure
           * is somebody closing their result card — the takeover the server still runs then is not a
           * game changing under anybody, so «…ربات جایگزینش شد» (and, for an inherited crown, «…و
           * بازی ادامه دارد») would be false, and the chime would land on the result screen.
           *
           * ⚠️ BUT NEVER DROPPED. An old game-server sends no `rematch-state`; this frame and the
           * roster's bot flip are the only way the rematch decision learns that the owner — or every
           * other player — has gone. See {@link OnlineState.endDepartures}.
           */
          if (store.getState().result !== null) {
            store.setState((s) => ({ endDepartures: [...s.endDepartures, info] }));
            return;
          }
          // IS THIS ABOUT US? Until idle-removal shipped it never could be — the server tells the
          // TABLE about a departure and the departed player is no longer at it. A player removed for
          // inactivity IS still sitting there watching, so their own client now receives a `left`
          // naming their own seat, and it must not render as a banner about somebody else.
          const mySeat = store.getState().seat;
          if (info.reason === "idle" && mySeat !== null && info.seat === mySeat) {
            playSfx("notify");
            store.setState({ removedForIdle: info, reconnecting: null, paused: null });
            return;
          }
          // Brief "X left — a bot took over" banner, then auto-dismiss. The "bot"
          // abandon policy (all quick-match tables) is otherwise completely silent.
          //
          // AND A SOUND. Someone leaving mid-hand changes the game you are playing — you
          // should not have to notice a banner to find out.
          playSfx("notify");
          // A bot took over (bot policy) → the "opponent reconnecting" overlay is done; show the banner.
          store.setState({ leftNotice: info, reconnecting: null });
          setTimeout(() => {
            store.setState((s) => (s.leftNotice === info ? { leftNotice: null } : {}));
          }, 5000);
          // THE GRACE RAN OUT and the chair is a bot's — the one moment «absent for 60 seconds» is true.
          // A «خروج», an idle removal or a kick is not that, whatever the exit costs afterwards.
          if (info.reason === "disconnected") {
            store.setState({ freeExitNotice: info });
            setTimeout(() => {
              store.setState((s) => (s.freeExitNotice === info ? { freeExitNotice: null } : {}));
            }, FREE_EXIT_NOTICE_MS);
          }
        },
        // The host closed the table. This fires in the WAITING room (the host left before the deal)
        // AND MID-GAME (the host's permanent «خروج بدون بازگشت» cancels the whole table) — an
        // earlier comment here claimed the mid-game case just hands their seat to a bot, which
        // stopped being true when host-cancel shipped. Record WHICH it was so the notice can say so.
        onTableClosed: (by) => {
          playSfx("notify");
          store.setState((s) => ({ closedBy: by, closedMidGame: s.phase === "playing" }));
        },
        // The table auto-expired (never started within the idle window): show the notice + leave.
        onTableExpired: () => {
          playSfx("notify");
          store.setState({ expiredByIdle: true });
        },
        // FEATURE 3 — the server minted a seat-fill token for the host's invite. Surface it as
        // state; the container creates the friend-invite carrying it, then clears it.
        onSeatTokenMinted: (payload) => store.setState({ seatTokenMinted: payload }),
        // Join-by-id failures (an accepted invite / deep link into a gone or locked
        // room) get their clean Persian reason; anything else keeps its message.
        onError: (message) => {
          const reason = joinErrorFa(message) ?? localizedReason(message);
          store.setState({ phase: "error", error: reason });
          explainJoinFailure(message, reason);
        },
      },
      left.signal,
    )
      .then((h) => {
        if (left.signal.aborted) {
          // It landed after the player left — nobody is here to hold it. DROPPED, never left: they
          // pressed nothing that says «leave» (see OnlineHandle.drop).
          h.drop();
          return;
        }
        handle = h;
      })
      .catch((e: unknown) => {
        /*
         * A NEWER COPY OF THIS PLAYER HOLDS THE SEAT (card-74 round 2, #3) — the game-server's door said
         * so (the takeover code, which only the chair's own player is given). That is a takeover, not a
         * failed join: the same covering sentence as a let-go copy's, the ticket left to the copy
         * playing — and not the straight-back's silent way to the lobby.
         */
        if (isSeatMoved(e)) {
          store.setState({ takenOver: true, reconnecting: null, paused: null, claimable: null });
          return;
        }
        /*
         * A «ادامه بازی» RETURN TO A TABLE THAT IS OVER (session B, item 3). The connector asked the
         * table's fate on the first refusal and stopped waiting; say so at once — «این بازی تمام شده
         * است» (or closed / expired) — and, when the server recorded it, how it ended.
         */
        if (e instanceof TableOverError) {
          store.setState({
            phase: "error",
            error: tableFateLine(e.status.state),
            tableEnd: e.status.state === "finished" ? readTableEnd(e.status) : null,
          });
          return;
        }
        // Keep a localized server reason (gate/invite); a raw network/system string
        // → null, so the container shows its Persian `fa.online.connectionError`.
        const reason = joinErrorFa(e) ?? localizedReason(e);
        store.setState({ phase: "error", error: reason });
        explainJoinFailure(e, reason);
      });
  };
  if (!opts.deferConnect) connect();

  return store;
}

export type OnlineStore<M, V> = ReturnType<typeof createOnlineStore<M, V>>;
